import { Box, Grid, Link, Stack, Typography } from '@mui/material'
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion'
import { useTranslation } from 'react-i18next'
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary'
import MuiAccordionDetails from '@mui/material/AccordionDetails'
import { styled, useTheme } from '@mui/material/styles'
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp'
import { FAQs, fsColors } from '../../shared/constants'
import FsDashPlayer from '../../components/common/molecules/DashPlayer'
import { getIcon } from '../../components/common/Icons'

const FsHelpView = () => {
  const theme = useTheme()
  const { t, i18n } = useTranslation()

  const Accordion = styled((aprops: AccordionProps) => (
    <MuiAccordion disableGutters elevation={0} square {...aprops} />
  ))(() => ({
    border: `1px solid ${theme.palette.divider}`,
    '&:first-of-type': {
      borderTopLeftRadius: 8,
      borderTopRightRadius: 8,
    },
    '&:not(:last-of-type)': {
      borderBottom: 0,
    },
    '&:last-of-type': {
      borderBottomLeftRadius: 8,
      borderBottomRightRadius: 8,
    },
    '&:before': {
      display: 'none',
    },
  }))

  const AccordionSummary = styled((aprops: AccordionSummaryProps) => (
    <MuiAccordionSummary
      expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '1rem' }} />}
      {...aprops}
    />
  ))(() => ({
    backgroundColor: theme.palette.action.hover,
    flexDirection: 'row-reverse',
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
      transform: 'rotate(90deg)',
    },
    '& .MuiAccordionSummary-content': {
      marginLeft: theme.spacing(1),
    },
  }))

  const AccordionDetails = styled(MuiAccordionDetails)(() => ({
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  }))

  return (
    <>
      <Stack mb={1} direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center">
          {getIcon('help')}
          <Typography variant="h1" ml={1}>
            {t('help.center')}
          </Typography>
        </Stack>
      </Stack>

      <Box mx={4} sx={{ overflowY: 'auto', height: 'calc(100vh - 143px)' }}>
        <Box>
          <Typography variant="h3" className="my-5">
            {t('video.tutorial')}
          </Typography>
          <Box m={2} sx={{ maxWidth: 1000 }}>
            <FsDashPlayer />
          </Box>
        </Box>
        <Box mt={4}>
          <Typography variant="h3" className="my-5">
            {t('faqs')}
          </Typography>
          {Object.entries(FAQs[i18n.language as 'de' | 'en']).map(([key, value]) => (
            <Accordion key={key}>
              <AccordionSummary>
                <Typography>{key}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  {value.map((v, index) => (
                    <Box key={`${key}_${index}`} sx={{ mt: index === 0 ? 0 : 2 }}>
                      <Stack direction="row" alignItems="flex-start" justifyContent="flex-start">
                        <Typography className="font-semibold" mr={1}>
                          {index + 1}.
                        </Typography>
                        <Typography className="font-semibold" mr={1}>
                          {v.q}
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="flex-start" justifyContent="flex-start">
                        <Typography className="font-semibold" mr={1.3}>
                          A:
                        </Typography>
                        <Typography>{v.a}</Typography>
                      </Stack>
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
          <Typography textAlign="center" mt={2}>
            {i18n.language === 'en'
              ? 'More questions? Please contact us at'
              : 'Noch Fragen? Bitte kontaktieren Sie uns unter'}{' '}
            <Link href="mailto:support.fs@ines-analytics.com">support.fs@ines-analytics.com</Link>{' '}
            {i18n.language === 'en' ? 'for further queries.' : 'für weitere Fragen.'}
          </Typography>
        </Box>
      </Box>
    </>
  )
}

export default FsHelpView
